import { Fragment, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { tooltipClasses } from '@mui/material/Tooltip';
import Iconify from 'src/components/iconify';
import AssetsDialog from './AssetsDialog';
import { LanguageTabs, TabPanel } from '../language-tabs';
import { visibleLanguages } from 'src/config/quill-text-editor-settings';
import { OptionType } from '@/@types/api.types.ts';

const defaultState = {
  title_en: '',
  description_en: '',
  title_fr: '',
  description_fr: '',
  data: {},
} as unknown as OptionType;

const HtmlTooltip = styled(({ className, ...props }: any) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));

const InputTooltip = ({ imgPath }: any) => (
  <HtmlTooltip
    title={
      <Fragment>
        <img src={imgPath} alt='login' />
      </Fragment>
    }
  >
    <Iconify icon='eva:info-outline' sx={{}} />
  </HtmlTooltip>
);

export default function OptionDialog(props: any) {
  const [option, setOption] = useState<OptionType>(defaultState);
  const [assetsTarget, setAssetsTarget] = useState<string | null>(null);

  useEffect(() => {
    if (!props.option) return;

    setOption(props.option);
  }, [props]);

  const handleChange = (e: any) => {
    const { id, value } = e.target;

    setOption((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const [value, setValue] = useState(0);

  const handleChangeTabs = (_event: any, newValue: number) => {
    setValue(newValue);
  };

  const deleteImage = (key: string) => {
    setOption((prevState) => ({
      ...prevState,
      [key]: null,
    }));
  };

  const onSuccess = () => {
    setOption(defaultState);
    props.onSuccessHandler(option);
  };

  const onCancel = () => {
    setOption(defaultState);
    props.onClose();
  };

  const onChoseAsset = (
    assetUrl = '',
    title_en = null,
    title_fr = null,
    description_en = null,
    description_fr = null,
    jersey_number = null,
    option_secondary_image = null,
    teamId = null,
    playerId = null
  ) => {
    setOption((prevState) => ({
      ...prevState,
      [String(assetsTarget)]: assetUrl,
    }));

    if (
      assetsTarget === 'option_image' &&
      title_en !== null &&
      title_fr !== null
    ) {
      setOption((prevState) => ({
        ...prevState,
        title_en,
        title_fr,
      }));
    }

    if (
      assetsTarget === 'option_secondary_image' &&
      title_en !== null &&
      title_fr !== null
    ) {
      setOption((prevState) => ({
        ...prevState,
        additional_title_en: title_en,
        additional_title_fr: title_fr,
      }));
    }

    if (
      assetsTarget === 'option_image' &&
      description_en !== null &&
      description_fr !== null
    ) {
      setOption((prevState) => ({
        ...prevState,
        description_en,
        description_fr,
      }));
    }

    if (assetsTarget === 'option_image' && jersey_number !== null) {
      setOption((prevState) => ({
        ...prevState,
        data: { ...prevState.data, jersey_number },
      }));
    }

    if (assetsTarget === 'option_image' && option_secondary_image !== null) {
      setOption((prevState) => ({
        ...prevState,
        option_third_image: option_secondary_image,
      }));
    }

    if (teamId !== null) {
      setOption((prevState) => ({
        ...prevState,
        data: { ...prevState.data, team_id: teamId },
      }));
    }
    setOption((prevState) => ({
      ...prevState,
      data: { ...prevState.data, player_id: playerId },
    }));
    setAssetsTarget(null);
  };

  const onCloseAssetsDialog = () => {
    setAssetsTarget(null);
  };

  return (
    <>
      {assetsTarget === null ? (
        <Dialog
          open={props.open}
          onClose={() => {}}
          aria-labelledby='alert-dialog-title'
          aria-describedby='alert-dialog-description'
          fullWidth
          maxWidth='md'
        >
          <DialogTitle id='alert-dialog-title'>Add Option</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Grid container>
                  <Grid item xs={12} sx={{ textAlign: 'center' }}>
                    Main Image
                    <InputTooltip imgPath='/assets/tooltips/main-image.png' />
                  </Grid>
                  <Grid item xs={12} sx={{ textAlign: 'center' }}>
                    {option.option_image ? (
                      <>
                        <Avatar
                          sx={{ margin: '0 auto', width: 100, height: 100 }}
                          alt='Remy Sharp'
                          src={option.option_image}
                        />
                        <Iconify
                          icon='eva:edit-outline'
                          sx={{ width: 30, height: 30, cursor: 'pointer' }}
                          onClick={() => setAssetsTarget('option_image')}
                        />
                        <Iconify
                          icon='eva:close-outline'
                          sx={{ width: 30, height: 30, cursor: 'pointer' }}
                          onClick={() => deleteImage('option_image')}
                        />
                      </>
                    ) : (
                      <Iconify
                        icon='eva:plus-circle-outline'
                        sx={{
                          width: 30,
                          height: 30,
                          cursor: 'pointer',
                          marginTop: 4,
                          marginBottom: 4,
                        }}
                        onClick={() => setAssetsTarget('option_image')}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <Grid container>
                  <Grid item xs={12} sx={{ textAlign: 'center' }}>
                    Secondary Image
                    <InputTooltip imgPath='/assets/tooltips/secondary-image.png' />
                  </Grid>
                  <Grid item xs={12} sx={{ textAlign: 'center' }}>
                    {option.option_secondary_image ? (
                      <>
                        <Avatar
                          sx={{ margin: '0 auto', width: 100, height: 100 }}
                          alt='Remy Sharp'
                          src={option.option_secondary_image}
                        />
                        <Iconify
                          icon='eva:edit-outline'
                          sx={{ width: 30, height: 30, cursor: 'pointer' }}
                          onClick={() =>
                            setAssetsTarget('option_secondary_image')
                          }
                        />
                        <Iconify
                          icon='eva:close-outline'
                          sx={{ width: 30, height: 30, cursor: 'pointer' }}
                          onClick={() => deleteImage('option_secondary_image')}
                        />
                      </>
                    ) : (
                      <Iconify
                        icon='eva:plus-circle-outline'
                        sx={{
                          width: 30,
                          height: 30,
                          cursor: 'pointer',
                          marginTop: 4,
                          marginBottom: 4,
                        }}
                        onClick={() =>
                          setAssetsTarget('option_secondary_image')
                        }
                      />
                    )}
                  </Grid>
                </Grid>
                <TextField
                  id='odds_marketing_id'
                  label='Odds Marketing Id'
                  fullWidth
                  value={option.odds_marketing_id}
                  variant='standard'
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    width: '100%',
                    borderBottom: 1,
                    borderTop: 1,
                    borderColor: 'divider',
                    mt: 2,
                  }}
                >
                  <LanguageTabs value={value} onChange={handleChangeTabs} />
                  {visibleLanguages.map((lang, index) => (
                    <TabPanel key={lang.code} value={value} index={index}>
                      <TextField
                        id={`title_${lang.code}`}
                        label={`Title ${lang.code}`}
                        fullWidth
                        value={option[`title_${lang.code}`]}
                        variant='standard'
                        onChange={handleChange}
                      />
                      <TextField
                        id={`description_${lang.code}`}
                        label={`Description ${lang.code}`}
                        fullWidth
                        value={option[`description_${lang.code}`]}
                        variant='standard'
                        onChange={handleChange}
                      />
                    </TabPanel>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onCancel()}>Cancel</Button>
            <Button onClick={() => onSuccess()} autoFocus>
              {props.option === null ? 'Add' : 'Edit'}
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <AssetsDialog
          onSuccess={onChoseAsset as any}
          onClose={onCloseAssetsDialog}
        />
      )}
    </>
  );
}
